import { ColumnWithLooseAccessor } from 'react-table';
import {
  ExtraFieldFormInfoFragment,
  ExtraFieldsTable_ExtraFieldValueFragment,
  ExtraFieldsTable_WithExtraFieldsFragment,
  FieldType,
} from '../../__generated__/graphql';

// that file acts like a documentation for all supported context params for extra fields
export const extraFieldsContextParams = {
  absenceRequestForm: {
    absenceCategoryName: 'absenceCategoryName',
    employeeAbsenceCategory: 'employeeAbsenceCategory',
  },
  projectTimeEntryEdit: {
    day: 'day',
    project: 'project',
    employee: 'employee',
  },
} as const;

export type ExtraFieldContextParam = {
  name: string;
  type: FieldType;
  value: string;
};

// Please always update ExtraFields API Reference
// https://www.notion.so/flairhr/Flair-Extra-fields-API-reference-1b69082b76de803993e1f5c756b103d3

/* eslint-disable no-unused-vars */
export enum ExtraFieldsSupportedObject {
  Expense = 'flair__Expense__c',
  ExpenseItem = 'flair__Expense_Item__c',
  Absence = 'flair__Absence__c',
  Project = 'flair__Project__c',
  ProjectTimeEntry = 'flair__Project_Time_Entry__c',
  Employee = 'flair__Employee__c',
  CertificateAssociation = 'flair__Certificate_Association__c',
}
export const allExtraFieldsSupportedObjects = Object.values(
  ExtraFieldsSupportedObject,
);

// Please always update ExtraFields API Reference
// https://www.notion.so/flairhr/Flair-Extra-fields-API-reference-1b69082b76de803993e1f5c756b103d3
export enum ExtraFieldsTableId {
  AbsencesMy = 'flair__Absence__c.MyAbsences',
  AbsencesManager = 'flair__Absence__c.ManagerAbsences',
  ExpensesMy = 'flair__Expense__c.MyExpenses',
  ExpensesManager = 'flair__Expense__c.ManagerExpenses',
  Directory = 'flair__Employee__c.Directory',
}

// Please always update ExtraFields API Reference
// https://www.notion.so/flairhr/Flair-Extra-fields-API-reference-1b69082b76de803993e1f5c756b103d3
export enum ExtraFieldsFormId {
  AbsencesRequestAbsence = 'flair__Absence__c.RequestAbsence',
  AbsencesManagerDetails = 'flair__Absence__c.ManagerDetails',
  ExpensesRequestExpense = 'flair__Expense__c.RequestExpense',
  ExpensesDetails = 'flair__Expense__c.Details',
  ExpensesRequestExpenseItem = 'flair__Expense_Item__c.RequestExpenseItem',
  ProjectsUserDetails = 'flair__Project__c.UserDetails',
  ProjectsManagerDetails = 'flair__Project__c.ManagerDetails',
  ProjectsTimeEntryEdit = 'flair__Project_Time_Entry__c.Edit',
  EmployeeDirectoryDetails = 'flair__Employee__c.DirectoryDetails',
  CertificatesSave = 'flair__Certificate_Association__c.Save',
}

export const ExtraFieldsTableCustomColumns = {
  absences: {
    name: '$custom.name',
    absenceCategory: '$custom.absenceCategory',
    approver: '$custom.approver',
    comments: '$custom.comments',
    employee: '$custom.employee',
    loom: '$custom.loom',
  },
  expenses: {
    amount: '$custom.amount',
    items: '$custom.items',
    comments: '$custom.comments',
    deleteAction: '$custom.deleteAction',
  },
} as const;

export type ExtraTableRenderSettings<T extends RecordWithExtraFields> = {
  columns: Record<string, ColumnRenderSettings<T>>;
  beforePersistentColumns?: Record<string, ColumnRenderSettings<T>>;
  afterPersistentColumns?: Record<string, ColumnRenderSettings<T>>;
  responsibleMobileColumns?: Record<string, ColumnRenderSettings<T>>;
};

export type ColumnRenderSettings<T extends RecordWithExtraFields> = Omit<
  ColumnWithLooseAccessor<ExtraTableItemType<T>>,
  'accessor'
>;

export type RecordWithExtraFields = { Id: string } & Record<string, any> &
  ExtraFieldsTable_WithExtraFieldsFragment;

export type ExtraTableItemType<TData extends RecordWithExtraFields> = {
  rawExtraFields: Record<
    string,
    ExtraFieldsTable_ExtraFieldValueFragment | null
  >;
} & TData;

export type ExtraFieldFormInfoType = ExtraFieldFormInfoFragment & {
  disabled?: boolean;
};
